import * as THREE from "three";
import { CollisionPlane } from "./CollisionPlane";
import Direction from "./Enums/Direction";
import Coordinate from "./Enums/Coordinate";

class Movement {
    collisionPlane: CollisionPlane;
    interactionRange: CollisionPlane;
    speed: number = 0; // might make protected

    constructor(collisionPlane: CollisionPlane, interactionRange?: CollisionPlane) {
        this.collisionPlane = collisionPlane;
        this.interactionRange = interactionRange ?? new CollisionPlane({
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        });
    }

    protected oppositeDirection(direction: Direction): Direction {
        switch (direction) {
            case Direction.Up:
                return Direction.Down;
            case Direction.Down:
                return Direction.Up;
            case Direction.Right:
                return Direction.Left;
            case Direction.Left:
                return Direction.Right;
        }
    }

    protected clockwiseDirection(direction: Direction): Direction {
        switch (direction) {
            case Direction.Up:
                return Direction.Right;
            case Direction.Down:
                return Direction.Left;
            case Direction.Right:
                return Direction.Down;
            case Direction.Left:
                return Direction.Up;
        }
    }

    protected determineCoordinate(direction: Direction): Coordinate {
        switch (direction) {
            case Direction.Up:
                return Coordinate.Y;
            case Direction.Down:
                return Coordinate.Y;
            case Direction.Right:
                return Coordinate.X;
            case Direction.Left:
                return Coordinate.X;
        }
    }

    protected newPosition(direction: Direction, position: THREE.Vector3): number {
        switch (direction) {
            case Direction.Up:
                return position.y + this.speed;
            case Direction.Down:
                return position.y - this.speed;
            case Direction.Right:
                return position.x + this.speed;
            case Direction.Left:
                return position.x - this.speed;
        }
    }

    private perpendicularOverlap(collision: CollisionPlane, coordinate: Coordinate): number {
        const playerPos = this.collisionPlane.position(coordinate);
        const playerSize = this.collisionPlane.size();
        const playerHalf = coordinate === Coordinate.Y ? playerSize.height / 2 : playerSize.width / 2;
        const wallPos = collision.position(coordinate);
        const wallSize = collision.size();
        const wallHalf = coordinate === Coordinate.Y ? wallSize.height / 2 : wallSize.width / 2;
        return Math.min(playerPos + playerHalf, wallPos + wallHalf) - Math.max(playerPos - playerHalf, wallPos - wallHalf);
    }

    private collisionByDirection(direction: Direction, collision: CollisionPlane): boolean {
        const size = this.collisionPlane.size();
        switch (direction) {
            case Direction.Up:
                return this.perpendicularOverlap(collision, Coordinate.X) > this.speed / 2 &&
                       collision.collisionEdge(direction) >= this.collisionPlane.position(Coordinate.Y) - size.height / 2;
            case Direction.Down:
                return this.perpendicularOverlap(collision, Coordinate.X) > this.speed / 2 &&
                       collision.collisionEdge(direction) <= this.collisionPlane.position(Coordinate.Y) + size.height / 2;
            case Direction.Right:
                return this.perpendicularOverlap(collision, Coordinate.Y) > this.speed / 2 &&
                       collision.collisionEdge(direction) >= this.collisionPlane.position(Coordinate.X) - size.width / 2;
            case Direction.Left:
                return this.perpendicularOverlap(collision, Coordinate.Y) > this.speed / 2 &&
                       collision.collisionEdge(direction) <= this.collisionPlane.position(Coordinate.X) + size.width / 2;
        }
    }

    private isMovementTooLarge(collisionLimit: number, currentPosition: number){
        const difference = Math.abs(collisionLimit) - Math.abs(currentPosition);
        return Math.abs(difference) > this.speed * 2
    }

    protected buffer(direction: Direction): number{
        switch (direction) {
            case Direction.Up:
                return this.speed / -2;
            case Direction.Down:
                return this.speed / 2;
            case Direction.Right:
                return this.speed / -2;
            case Direction.Left:
                return this.speed / 2;
        }
    }

    protected collisionLimit(collision: CollisionPlane, direction: Direction): number {
        const size = this.collisionPlane.size();
        const currentPosition = this.collisionPlane.mesh.position;
        const buffer = this.buffer(direction);
        let collisionLimit: number;
        switch (direction) {
            case Direction.Up:
                collisionLimit = collision.collisionEdge(direction) - (size.height / 2);
                return (this.isMovementTooLarge(collisionLimit, currentPosition.y) ? currentPosition.y : collisionLimit) + buffer;
            case Direction.Down:
                collisionLimit = collision.collisionEdge(direction) + (size.height / 2);
                return (this.isMovementTooLarge(collisionLimit, currentPosition.y) ? currentPosition.y : collisionLimit) + buffer;
            case Direction.Right:
                collisionLimit = collision.collisionEdge(direction) - (size.width / 2);
                return (this.isMovementTooLarge(collisionLimit, currentPosition.x) ? currentPosition.x : collisionLimit) + buffer;
            case Direction.Left:
                collisionLimit = collision.collisionEdge(direction) + (size.width / 2);
                return (this.isMovementTooLarge(collisionLimit, currentPosition.x) ? currentPosition.x : collisionLimit) + buffer;
        }
    }

    protected detectCollision(collisions: CollisionPlane[], direction?: Direction,
                              collisionAction?: (collision: CollisionPlane) => void,
                              interactionAction = (collision: CollisionPlane): void => { collision.userData() }
                              ): boolean {
        this.collisionPlane.detectWorld();
        if (this.interactionRange) this.interactionRange.detectWorld();

        for (let i = 0; i < collisions.length; i++) {
            const collision = collisions[i];
            collision.detectWorld();
            if (collision.isInteractable() && this.interactionRange && this.interactionRange.intersects(collision)) {
                interactionAction(collision);
            }
            if (this.collisionPlane.intersects(collision)) {
                if (direction) {
                    if (this.collisionByDirection(direction, collision)) {
                        if (collisionAction) collisionAction(collision);
                        return true;
                    }
                } else {
                    return true;
                }
            }
        }
        return false;
    };
}
export default Movement;